<script setup lang="ts">
//#region imports
import { computed, ref, watch } from 'vue';
import { useLodash } from '@/setup/lodash';
import DashboardFilter from '@/views/Dashboard/components/DashboardFilter.vue';
import { useDashboardFilter } from '@/composables/useDashboardFilter';
import type { FilterRecord } from '@/types/core';
import type { FilterRecordExtendedCustom } from '@/types/dashboard';
import type { CurrentFilter } from '@/views/Dashboard/components/DashboardFilter.vue';
//#endregion

//#region types
export interface Props {
  setCurrentSearch: FilterRecord,
  disabledMode?: boolean,
}

export interface Emits {
  'submit': [filter: FilterRecordExtendedCustom, search: FilterRecord],
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  disabledMode: false,
});

const emit = defineEmits<Emits>();

const modelValue = defineModel<FilterRecordExtendedCustom>({
  default: () => ({}),
});
//#endregion

//#region composables
const _ = useLodash();
const { transformForFilterView, transformFromFilterView, isDisabled, handleDisabled }  = useDashboardFilter();
//#endregion

//#region state
const currentSearch = ref(_.cloneDeep(props.setCurrentSearch));
const currentFilters = ref<CurrentFilter[]>([]);

//#endregion

//#region computed
const isDisabledTab = computed(() => {
  return _.isEmpty(currentSearch.value);
});

const isDisabledApply = computed(() => {
  return (
    (isDisabled.value && currentFilters.value.length !== 0)
    || (isDisabledTab.value && currentFilters.value.length === 0));
});
//#endregion

//#region methods


function handleApply() {
  const val: FilterRecordExtendedCustom = transformFromFilterView(currentFilters.value);

  modelValue.value = val;
  emit('submit', val, currentSearch.value);
}

function handleChangeFilterTab(search: FilterRecord) {
  currentSearch.value = search;
}

function handleOpen() {
  currentFilters.value = transformForFilterView(modelValue.value);
}

function handleReset() {
  currentFilters.value = [];
  currentSearch.value = {};
  modelValue.value = {};
  emit('submit', {}, {});
}
//#endregion

//#region watch
watch(currentFilters, (v) => {
  handleDisabled(v);
}, {
  deep: true,
});
//#endregion
</script>

<template>
  <UiFilterElement
    class="dashboard-global-filter"
    :disable="disabledMode"
    :is-active-button="!isDisabledApply"
    :has-filters="!_.isEmpty(modelValue) || !_.isEmpty(setCurrentSearch)"
    @open="handleOpen"
    @apply="handleApply"
    @reset="handleReset"
  >
    <DashboardFilter
      v-model="currentFilters"
      :set-current-search="setCurrentSearch"
      @change-filter-tab="handleChangeFilterTab"
    />
  </UiFilterElement>
</template>

<style lang="scss">
.dashboard-global-filter {
  .ui-filter-element__menu {
    padding: 16px 14px;
  }
  .ui-filter-element__controls {
    margin: 0;
  }
}
</style>
